import {
  Building2,
  Calendar,
  Globe,
  Hash,
  Monitor,
  Briefcase,
  Tag,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { InfoItem, infoItemIconClass as iconClass } from "./components/InfoItem";
import { InfoItemSkeleton } from "./components/InfoItemSkeleton";
import { AppliesIntegrationSections } from "./sections";
import { useApply } from "../../api/useApply";
import { formatApplicationSourceTypes } from "../../utils/applicationSourceType";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Badge } from "@/components/ui/v2/badge";
import { Skeleton } from "@/components/ui/v2/skeleton";

export default function ApplyDetailPage() {
  const { applyId } = useParams<{ applyId: string }>();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useApply(applyId);

  // Deep links are built server-side and arrive on the responses (null → plain text).
  const publicationUrl = data?.jobSourceUrl ?? null;

  if (isLoading) {
    return (
      <ModernPageLayout
        title=""
        backbutton={{ label: t("common.applies"), href: ".." }}
      >
        <Skeleton className="h-8 w-64" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-6 w-40" />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <InfoItemSkeleton />
              <InfoItemSkeleton />
              <InfoItemSkeleton />
              <InfoItemSkeleton />
            </div>
          </div>
        ))}
      </ModernPageLayout>
    );
  }

  if (isError || (!isLoading && !data)) {
    return (
      <ModernPageLayout
        title={t("common.notFound")}
        backbutton={{ label: t("common.applies"), href: ".." }}
      >
        <p className="text-muted-foreground">{t("integrations.cockpit.applies.detail.notFound")}</p>
      </ModernPageLayout>
    );
  }

  const title = data?.vacancyTitle ?? data?.vacancyId ?? t("common.applies");

  return (
    <ModernPageLayout
      title={title}
      badge={data?.vacancyId ? <Badge variant="outline" size="medium">{data.vacancyId}</Badge> : undefined}
      backbutton={{ label: t("common.applies"), href: ".." }}
    >
      {/* Application Details */}
      <section>
        <h2 className="mb-3 text-lg font-semibold">
          {t("integrations.cockpit.applies.detail.applicationDetails")}
        </h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <InfoItem
            icon={<Calendar className={iconClass} />}
            label={t("common.dateSubmitted")}
            value={data?.dateSubmitted ? new Date(data.dateSubmitted).toLocaleString() : null}
          />
          <InfoItem
            icon={<Building2 className={iconClass} />}
            label={t("common.client")}
            value={data?.clientName}
          />
          <InfoItem
            icon={<Briefcase className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.vacancyTitle")}
            value={data?.vacancyTitle}
          />
          <InfoItem
            icon={<Globe className={iconClass} />}
            label={t("common.jobBoard")}
            value={data?.jobBoardName ? t(data.jobBoardName) : undefined}
          />
          <InfoItem
            icon={<Tag className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.applicationSources")}
            value={formatApplicationSourceTypes(data?.applicationSourceTypes, t)}
          />
        </div>
      </section>

      {/* Identifiers */}
      <section>
        <h2 className="mb-3 text-lg font-semibold">
          {t("integrations.cockpit.applies.detail.identifiers")}
        </h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <InfoItem
            icon={<Hash className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.applyId")}
            value={data?.id}
          />
          <InfoItem
            icon={<Hash className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.originId")}
            value={data?.originId}
          />
          <InfoItem
            icon={<Hash className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.vacancyId")}
            value={data?.vacancyId}
          />
          <InfoItem
            icon={<Hash className={iconClass} />}
            label={t("common.publicationId")}
            value={data?.publicationId}
            href={publicationUrl}
          />
        </div>
      </section>

      {/* What each integration with an active applies module contributes: identifiers, warnings, actions */}
      {data && <AppliesIntegrationSections apply={data} />}

      {/* UTM Parameters */}
      <section>
        <h2 className="mb-3 text-lg font-semibold">
          {t("integrations.cockpit.applies.detail.utmParameters")}
        </h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <InfoItem
            icon={<Hash className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.utmId")}
            value={data?.utmId}
          />
          <InfoItem
            icon={<Briefcase className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.utmCampaign")}
            value={data?.utmCampaign}
          />
          <InfoItem
            icon={<Globe className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.utmSource")}
            value={data?.utmSource}
          />
          <InfoItem
            icon={<Monitor className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.utmMedium")}
            value={data?.utmMedium}
          />
          <InfoItem
            icon={<Calendar className={iconClass} />}
            label={t("integrations.cockpit.applies.detail.utmTerm")}
            value={data?.utmTerm}
          />
        </div>
      </section>
    </ModernPageLayout>
  );
}
